import ApexCharts from "apexcharts";
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy, ViewChild, ElementRef, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SummaryModalService } from "src/app/services/performance-service/summary-modal.service";
import { FindingDetailsModalService } from "src/app/services/performance-service/finding-details-modal.service";
import { Subscription } from "rxjs";
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: "app-summary-modal",
  standalone: true,
  imports: [
    TranslateModule,CommonModule],
  templateUrl: "./summary-modal.component.html",
  styleUrls: ["./summary-modal.component.scss"],
})
export class SummaryModalComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closeModal = new EventEmitter<void>();

  @ViewChild("hseChartEl") hseChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("ltiChartEl") ltiChartEl!: ElementRef<HTMLDivElement>;
  @ViewChild("creChartEl") creChartEl!: ElementRef<HTMLDivElement>;

  private hseChart: ApexCharts | null = null;
  private ltiChart: ApexCharts | null = null;
  private creChart: ApexCharts | null = null;

  private summaryModalService      = inject(SummaryModalService);
  private findingDetailsModalService = inject(FindingDetailsModalService);
  private dataSub: Subscription | null = null;

  creTableData: any[] = [];

  // Displayed scores (updated from service data)
  hseScore: number = 0;
  ltiScore: number | null = null;
  creScore: number = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (changes["isOpen"]) {
      if (this.isOpen) {
        // Wait for *ngIf to render the modal DOM, then build charts
        setTimeout(() => this.subscribeAndBuild(), 0);
      } else {
        this.dataSub?.unsubscribe();
        this.dataSub = null;
        this.destroyCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.destroyCharts();
  }

  close() {
    this.closeModal.emit();
  }

  private subscribeAndBuild(): void {
    this.dataSub?.unsubscribe();
    this.dataSub = this.summaryModalService.chartData$.subscribe((data) => {
      if (data) {
        this.hseScore = data.hseScore;
        this.ltiScore = data.ltiScore;
        this.creScore = data.creScore;
        if (data.summaryRow)      this.summaryRow      = data.summaryRow;
        if (data.recentLtis)      this.recentLtis      = data.recentLtis;
        if (data.overdueFindings) this.overdueFindings  = data.overdueFindings;
        if (data.creTableData)    this.creTableData     = data.creTableData;
        if (data.aiText)          this.aiText           = data.aiText;
        this.buildCharts(data.hseData, data.ltiData, data.creData);
      } else {
        this.buildCharts([], [], []);
      }
    });
  }

  private buildCharts(hseData: number[], ltiData: number[], creData: number[]): void {
    this.destroyCharts();

    if (this.hseChartEl?.nativeElement) {
      this.hseChart = new ApexCharts(
        this.hseChartEl.nativeElement,
        this.sparklineOptions(hseData, this.lineColor(this.hseScore))
      );
      this.hseChart.render();
    }

    if (this.ltiChartEl?.nativeElement) {
      this.ltiChart = new ApexCharts(
        this.ltiChartEl.nativeElement,
        this.sparklineOptions(ltiData, this.lineColor(this.ltiScore ?? 0))
      );
      this.ltiChart.render();
    }

    if (this.creChartEl?.nativeElement) {
      this.creChart = new ApexCharts(
        this.creChartEl.nativeElement,
        this.sparklineOptions(creData, this.lineColor(this.creScore))
      );
      this.creChart.render();
    }
  }

  private destroyCharts(): void {
    this.hseChart?.destroy();
    this.hseChart = null;
    this.ltiChart?.destroy();
    this.ltiChart = null;
    this.creChart?.destroy();
    this.creChart = null;
  }

  private sparklineOptions(data: number[], color: string): any {
    const max = data.length ? Math.max(...data) : 100;
    return {
      series: [{ name: "", data }],
      chart: {
        type: "line",
        height: 80,
        width: "100%",
        background: "transparent",
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        sparkline: { enabled: true },
      },
      stroke: { curve: "smooth", width: 1.5, colors: [color] },
      xaxis: {
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false, min: 0, max: max * 1.45 },
      grid: { show: false, padding: { right: 36, left: 0, top: 8, bottom: 6 } },
      markers: {
        size: 2.5,
        colors: [color],
        strokeWidth: 0,
        discrete: [
          { seriesIndex: 0, dataPointIndex: 0, fillColor: "transparent", strokeColor: "transparent", size: 0 },
        ],
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      tooltip: { enabled: false },
      colors: [color],
    };
  }

  /** Same threshold logic as performance-indicator-card getCommonTheme() */
  lineColor(score: number): string {
    if (score < 55)  return "#9F0014";
    if (score < 85)  return "#FFDE72";
    if (score < 95)  return "#94C12E";
    if (score <= 100) return "#009146";
    return "#800080";
  }

  chartBg(score: number): string {
    if (score < 55)   return 'linear-gradient(166deg, rgb(245,229,231) 3%, rgb(245,229,231) 44%, rgb(236,206,209) 63%, rgb(236,206,209) 84%)';
    if (score < 85)   return 'linear-gradient(166deg, rgba(255,249,229,1) 3%, rgba(255,249,229,1) 44%, rgba(255,244,206,1) 63%, rgba(255,244,206,1) 84%)';
    if (score < 95)   return 'linear-gradient(166deg, rgba(244,249,234,1) 3%, rgba(244,249,234,1) 44%, rgba(234,243,215,1) 63%, rgba(234,243,215,1) 84%)';
    if (score <= 100) return 'linear-gradient(166deg, rgba(229,244,236,1) 3%, rgba(229,244,236,1) 44%, rgba(206,234,219,1) 63%, rgba(206,234,219,1) 84%)';
    return 'linear-gradient(166deg, rgba(239,229,245,1) 3%, rgba(239,229,245,1) 44%, rgba(225,206,236,1) 63%, rgba(225,206,236,1) 84%)';
  }

  // ── Header ────────────────────────────────────────────────────────────
  headerIconSrc = "assets/performance/summary-gauge.svg";
  headerTitle   = "Summary";

  // ── AI Box ────────────────────────────────────────────────────────────
  aiText = '';

  // ── Summary Table ─────────────────────────────────────────────────────
  tableColumns = ["", "HSE-IP", "LTI", "CRE", "CIS"];

  summaryRow: { region: string; hseIp: number | null; lti: number | null; cre: number | null; cis: number | null; cisTrend: string } = {
    region:   '',
    hseIp:    null,
    lti:      null,
    cre:      null,
    cis:      null,
    cisTrend: 'flat',
  };

  // ── Mini Cards ────────────────────────────────────────────────────────
  hseCard = {
    iconSrc:  "assets/performance/hse-improvement.svg",
    iconAlt:  "HSE",
    title:    "HSE Improvement Plan",
    subtitle: "On-time objective closure",
  };

  ltiCard = {
    iconSrc:  "assets/performance/lti-shield.svg",
    iconAlt:  "LTI",
    title:    "Lost Time Injury Score",
    subtitle: "(Frequency rate)",
  };

  creCard = {
    iconSrc:  "assets/performance/cre-shield.svg",
    iconAlt:  "CRE",
    title:    "Critical Risk Elimination",
    subtitle: "On-time finding closure",
  };

  // ── Overdue Table ─────────────────────────────────────────────────────
  overdueTitle    = "Overdue CRE Findings:";
  overdueSubtitle = "Impacting country CIS";
  overdueColumns  = ["Date", "Location", "Level"];
  searchIconSrc   = "assets/performance/search-icon.svg";

  overdueFindings: Array<{ date: string; location: string; level: number; dateColor: string; findingNumber?: string }> = [];

  recentLtis: string[] = [];

  openFindingDetails(row: any): void {
    const match = this.creTableData.find(
      (d: any) => d.finding_number === row.findingNumber || d.location === row.location
    );
    if (match) {
      this.findingDetailsModalService.openWithCreRow(match);
    } else {
      this.findingDetailsModalService.open();
    }
  }
}
